import { useContactPropertyVisibleStore } from '../../store/contactPropertyVisibleStore';
import { getContactPropertyVisible, updateContactPropertyVisible } from '../../api/contactPropertyVisibleApi';
import { ContactPropertyVisibleData } from '../../dto/EntityRepository';
import { ContactPropertyVisibleResponse } from '../../dto/ResDtoRepository';
import { UpdateContactPropertyVisibleReq } from '../../dto/ReqDtoRepository';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';
import type { ResourceStatus } from '../../util/scopedResource';

interface UseContactPropertyVisibleStateResult {
  contactPropertyVisible: ContactPropertyVisibleData | null;
  visibleOwnerAui: string | null;
  status: ResourceStatus;
}

interface UseContactPropertyVisibleActionsResult {
  getContactPropertyVisible: () => Promise<void>;
  updateContactPropertyVisible: (data: UpdateContactPropertyVisibleReq) => Promise<void>;
}

export const useContactPropertyVisibleState = (): UseContactPropertyVisibleStateResult => {
  const contactPropertyVisible = useContactPropertyVisibleStore((s) => s.contactPropertyVisible);
  const visibleOwnerAui = useContactPropertyVisibleStore((s) => s.visibleOwnerAui);
  const status = useContactPropertyVisibleStore((s) => s.status);

  return { contactPropertyVisible, visibleOwnerAui, status };
};

export const useContactPropertyVisibleActions = (): UseContactPropertyVisibleActionsResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((s) => s.pageOwner?.aui);
  const beginContactPropertyVisibleFetch = useContactPropertyVisibleStore(
    (s) => s.beginContactPropertyVisibleFetch,
  );
  const setContactPropertyVisible = useContactPropertyVisibleStore(
    (s) => s.setContactPropertyVisible,
  );
  const setContactPropertyVisibleError = useContactPropertyVisibleStore(
    (s) => s.setContactPropertyVisibleError,
  );
  const setUpdatedTempAlert = useTempAlertStore((s) => s.setUpdatedTempAlert);
  const withApiHandler = useApiWrapper();

  const handleSuccess = (response: ContactPropertyVisibleResponse, ownerAui: string) => {
    setContactPropertyVisible(response.data, ownerAui);
  };
  const handleUpdateSuccess = (response: ContactPropertyVisibleResponse, ownerAui: string) => {
    setContactPropertyVisible(response.data, ownerAui);
    setUpdatedTempAlert();
  };

  const fetchContactPropertyVisible = async (aui: string) => {
    beginContactPropertyVisibleFetch(aui);
    try {
      return await runCurrentArchiveRequest(
        aui,
        () => getContactPropertyVisible(aui),
        (response) => handleSuccess(response, aui),
      );
    } catch (error) {
      setContactPropertyVisibleError();
      throw error;
    }
  };

  const handleContactPropertyVisibleRequest = async (
    action: 'get' | 'update',
    data?: UpdateContactPropertyVisibleReq | string
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'update':
          if (!data || typeof data === 'string') throw new Error('Valid data is required for update');
          const updateData = data satisfies UpdateContactPropertyVisibleReq;
          return runCurrentArchiveRequest(
            aui,
            () => updateContactPropertyVisible(aui, updateData),
            (response) => handleUpdateSuccess(response, aui),
          );
        case 'get':
        default:
          return fetchContactPropertyVisible(aui);
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  const getContactPropertyVisibleHandler = () => handleContactPropertyVisibleRequest('get');
  const updateContactPropertyVisibleHandler = (data: UpdateContactPropertyVisibleReq) =>
    handleContactPropertyVisibleRequest('update', data);

  return {
    getContactPropertyVisible: getContactPropertyVisibleHandler,
    updateContactPropertyVisible: updateContactPropertyVisibleHandler,
  };
};
